/**
 * READ-ORDER SSOT for the two transaction status fields that carry a legacy Zoho-cased twin beside their canonical D1
 * column. A PropertyDO transaction row is a union of both eras -- `tools/auction/state-helpers.ts` still writes
 * `Auction_Status` alongside `auction_status` -- so every reader must try BOTH spellings, in the same order, and a
 * reader that tries only one silently reports "no status" for half the corpus.
 *
 * That ordered key list was hand-spelled at eight call sites across `tools/`, `flows/` and the websocket parser, each
 * one an independent chance to drop the legacy arm or to reverse the precedence. The canonical column LEADS in both
 * tuples: it is the authoritative one, and the legacy twin is only the fallback for rows written before the migration.
 *
 * The `deal_status` family carries TWO legacy spellings because Zoho emitted both cases over its lifetime; both are
 * kept, in the order the surviving readers already used.
 *
 * Homed in `const/` rather than beside any one reader because the consumers sit in different tool clusters, in `flows/`
 * and in `utils/`, and a `tools/`->`tools/` import is a permanent ban (`QUAL-NO-CROSS-TOOL-IMPORTS-001`).
 */

import { FORM_FIELDS } from '@federa/const/reducer-actions';

/** The legacy Zoho-cased field names, as the only place each string is spelled.
 *
 *  MODULE-LOCAL, not exported: this record is the INGREDIENT, and the two `*_FIELD_KEYS` tuples below are what this
 *  leaf exists to hand out -- a reader spreads an ordered tuple, never a raw legacy name, which is the whole point of
 *  homing the read ORDER here. Exporting it added a name nothing imported: a `QUAL-DEAD-EXPORT-003` orphan export at
 *  ZERO occurrences anywhere outside this leaf, demoted 2026-08-22. Same shape as `SCANNER_FS_READ_TOKENS`
 *  (`const/scan-incremental-signals.ts`), cured the same way. */
const LEGACY_TRANSACTION_STATUS_FIELDS = {
	AUCTION_STATUS: 'Auction_Status',
	DEAL_STATUS: 'Deal_Status',
	/** Zoho's second, lower-cased-suffix spelling of the same field. */
	DEAL_STATUS_MIXED: 'Deal_status',
} as const;

/** The canonical `home_details.deal_status` column name -- no `FORM_FIELDS` member spells it.
 *
 *  EXPORTED, and the round trip is worth recording because it is the answer to the question the demotion asked. This
 *  was demoted to module-local 2026-08-22 as a `QUAL-DEAD-EXPORT-003` orphan export at ZERO external occurrences, with
 *  the note "export it the day a consumer genuinely needs the canonical column name WITHOUT the legacy fallbacks".
 *  That day was the SAME NIGHT: `components/lib/websocket/parse.ts` now reads
 *  `firstPresent(data, ...AUCTION_STATUS_FIELD_KEYS, DEAL_STATUS_FIELD, 'status')`, which wants the bare canonical
 *  column as ONE fallback among several rather than the deal-status read ORDER -- exactly the distinct need the note
 *  asked to be voiced. Re-exported 2026-08-23; the row drained by ADOPTION, which is the outcome the queue exists to
 *  drive, and the keyword is now carrying a real consumer instead of nothing. */
export const DEAL_STATUS_FIELD = 'deal_status';

/** Auction-status read order: canonical column, then the legacy twin. Spread into any variadic key reader. */
export const AUCTION_STATUS_FIELD_KEYS = [FORM_FIELDS.AUCTION_STATUS, LEGACY_TRANSACTION_STATUS_FIELDS.AUCTION_STATUS] as const;

/** Deal-status read order: canonical column, then both legacy spellings. Spread into any variadic key reader. */
export const DEAL_STATUS_FIELD_KEYS = [
	DEAL_STATUS_FIELD,
	LEGACY_TRANSACTION_STATUS_FIELDS.DEAL_STATUS,
	LEGACY_TRANSACTION_STATUS_FIELDS.DEAL_STATUS_MIXED,
] as const;
